import { useEffect, useState, useCallback } from 'react';
import { useQuery } from '@apollo/react-hooks';
import ScrollListener from './scrollListener';

const defaultOptions = {
  sessionId: null,
  query: null,
  limit: 24,
  type: 'products',
  poolling: false,
  pollInterval: 40,
  targetElement: '.product-grid__footer',
};

const useInfiniteScroll = (opts) => {
  const {
    sessionId,
    query,
    type,
    limit,
    poolling,
    pollInterval,
    targetElement,
  } = { ...defaultOptions, ...opts };

  if (!sessionId || !query || !type) {
    throw new Error('The parameters: sessionId, query and type are mandatory');
  }

  const pollingDefault = poolling ? { pollInterval } : {};
  const [pollingCouter, setPollingCouter] = useState(0);
  const [allLoaded, setAllLoaded] = useState(false);
  const [isPolling, setIsPolling] = useState(poolling);
  const [fetchinMore, setFetchingMore] = useState(false);
  const { data, error, fetchMore, loading, refetch, stopPolling } = useQuery(
    query,
    {
      variables: { sessionId, limit },
      fetchPolicy: 'network-only',
      ...pollingDefault,
      onCompleted: () => {
        setPollingCouter(pollingCouter + 1);
      },
    }
  );

  const onStopPooling = useCallback(() => {
    setIsPolling(false);
    stopPolling();
  }, [stopPolling]);

  const items = data ? data[type] : { nextToken: null };

  useEffect(() => {
    if (isPolling) {
      return;
    }

    if (items.nextToken) {
      setAllLoaded(false);
      ScrollListener.start(targetElement, () => {
        setFetchingMore(true);
        fetchMore({
          variables: {
            nextToken: items.nextToken,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            setFetchingMore(false);
            if (!fetchMoreResult) {
              return prevResult;
            }
            return {
              [type]: {
                items: [
                  ...prevResult[type].items,
                  ...fetchMoreResult[type].items,
                ],
                nextToken: fetchMoreResult[type].nextToken,
                // eslint-disable-next-line no-underscore-dangle
                __typename: prevResult[type].__typename,
              },
            };
          },
        });
      });
    } else {
      setAllLoaded(true);
    }
    // eslint-disable-next-line consistent-return
    return () => {
      ScrollListener.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, isPolling]);

  return {
    data,
    loading,
    error,
    refetch,
    allLoaded,
    stopPolling: onStopPooling,
    fetchinMore,
    pollingCouter,
  };
};

export default useInfiniteScroll;
